import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { listProducts } from "../api/products";
import { useAuth } from "../context/AuthContext";
import { useLanding } from "../context/LandingContext";
import { useProjectConfig } from "../context/ProjectConfigContext";
import { getProductImage, handleProductImageError } from "../utils/productMedia";
import { resolveLandingMedia } from "../utils/landingMedia";
import {
  formatDuration,
  getProductCategory,
  getProductSubcategory,
  getProductDescription,
  getServiceDuration,
  getProductCurrency,
  getProductId,
  getGastronomicMenu,
  getProductName,
  isProductActive,
  isService,
} from "../utils/productAdapter";
import { getCategoryLabel } from "../utils/categoryAdapter";
import { formatMoney } from "../utils/money";
import ProductPriceDisplay from "./ProductPriceDisplay";
import { catalogCardBackgroundWithOpacity, catalogCardButtonSx } from "../utils/catalogCardColors";
import { openConfiguredUrl } from "../utils/configuredNavigation";
import {
  catalogCardBoxShadow,
  catalogCardCtaLabel,
  catalogCardImagePosition,
  catalogCardTarget,
  normalizeCatalogCardConfig,
} from "../utils/catalogCardPresentation";
import { safeTextSx, safeTitleSx, sectionTitleFontSize, titleWordFitScale } from "../utils/textSafety";
import { scaleResponsiveCss, segmentVisualScale } from "../utils/segmentVisualStyle";
import { capitalizeSectionTitle } from "../utils/landingSectionText";
import {
  catalogItemLabel,
  getCatalogCapabilities,
  getCatalogItemKind,
  filterCatalogItems,
  resolveCatalogMode,
} from "../utils/catalogRouting";
import { featuredGastronomicMenuItems } from "../utils/gastronomicMenu";



export default function ProductCarousel({ sectionBackgroundSx = {} }) {
  const { pricingRevision } = useAuth();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down("md"));
  const { catalog, theme } = useLanding();
  const { payments, gastronomicMenuEnabled } = useProjectConfig();
  const copy = catalog?.featured || {};
  const gastronomicMode = catalog?.homeSource === "gastronomicMenu" && gastronomicMenuEnabled;
  const cardConfig = normalizeCatalogCardConfig(catalog?.cards);
  const cardBackgroundImage = resolveLandingMedia(cardConfig.backgroundImageUrl);
  const cardImageSide = cardConfig.imagePosition === "left" || cardConfig.imagePosition === "right";
  const cardImageRight = cardConfig.imagePosition === "right";
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [errMsg, setErrMsg] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErrMsg(null);
      try {
        const data = await listProducts(gastronomicMode
          ? { gastronomicMenu: true, page: 1, pageSize: 500, sortBy: "gastronomicMenu", sortDir: "asc" }
          : { page: 1, pageSize: 24, sortBy: "createdAt", sortDir: "desc" });
        if (alive) setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (error) {
        if (alive) setErrMsg(String(copy.errorText ?? error?.message ?? "No se pudieron cargar los productos."));
      } finally {
        if (alive) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      }
    })();
    return () => { alive = false; };
  }, [copy.errorText, gastronomicMode, pricingRevision]);

  const resolvedMode = useMemo(
    () => resolveCatalogMode(catalog?.mode, items),
    [catalog?.mode, items],
  );
  const products = useMemo(
    () => gastronomicMode
      ? featuredGastronomicMenuItems(items)
      : filterCatalogItems(items.filter(isProductActive), resolvedMode),
    [gastronomicMode, items, resolvedMode],
  );
  const repeated = products.length > 1 ? [...products, ...products] : products;
  const cardWidth = isMobile ? 230 : isTablet ? 252 : 280;
  const duration = isMobile ? 28 : isTablet ? 36 : 46;
  const sectionBackground = copy.backgroundColor || "#FFFFFF";
  const titleScale = segmentVisualScale(theme, "featured", "title");
  const imageScale = segmentVisualScale(theme, "featured", "image");
  const spacingScale = segmentVisualScale(theme, "featured", "spacing");
  const titleSize = scaleResponsiveCss(
    sectionTitleFontSize(copy.title, { xs: "2.15rem", md: "3rem" }),
    titleScale
  );
  const hasHeading = Boolean(copy.eyebrow || copy.title || copy.subtitle);
  const loadingText = String(copy.loadingText ?? "").trim();
  const emptyText = String(copy.emptyText ?? "").trim();
  const legacyCtaLabel = String(copy.ctaLabel ?? "").trim();
  const initialLoading = loading && !hasLoadedOnce;
  const visibleError = errMsg !== null && products.length === 0;

  if (!initialLoading && visibleError && !errMsg && !hasHeading) return null;
  if (!initialLoading && !visibleError && products.length === 0 && !hasHeading && !emptyText) return null;

  return (
    <Box className="storefront-section" sx={{ px: { xs: 2, sm: 3, md: 6 }, py: { xs: 5 * spacingScale, sm: 6 * spacingScale, md: 8 * spacingScale }, background: sectionBackground, overflow: "hidden", ...sectionBackgroundSx }}>
      <Box sx={{ maxWidth: 1440, mx: "auto" }}>
        {hasHeading ? (
          <Box sx={{ mb: { xs: 3, md: 4 } }}>
            {copy.eyebrow ? <Typography data-landing-field="catalog.featured.eyebrow" variant="overline" sx={{ ...safeTextSx, color: copy.eyebrowColor || "#F35A3A" }}>{copy.eyebrow}</Typography> : null}
            {copy.title ? <Typography data-landing-field="catalog.featured.title" variant="h3" sx={{ ...safeTitleSx, fontSize: titleSize, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)", color: copy.titleColor || "#19242F", mb: copy.subtitle ? 1 : 0 }}>{capitalizeSectionTitle(copy.title)}</Typography> : null}
            {copy.subtitle ? (
              <Typography data-landing-field="catalog.featured.subtitle" sx={{ ...safeTextSx, color: copy.subtitleColor || "#5B5D73", maxWidth: 760, lineHeight: 1.7, whiteSpace: "pre-line" }}>
                {copy.subtitle}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {initialLoading ? (
          loadingText ? (
            <Typography
              data-landing-field="catalog.featured.loadingText"
              sx={{ ...safeTextSx, color: copy.loadingTextColor || "#5B5D73" }}
            >
              {loadingText}
            </Typography>
          ) : null
        ) : visibleError ? (
          errMsg ? <Alert data-landing-field="catalog.featured.errorText" severity="error" sx={{ "& .MuiAlert-message": { color: copy.errorTextColor || "#D0333A" } }}>{errMsg}</Alert> : null
        ) : products.length === 0 ? (
          emptyText ? <Alert data-landing-field="catalog.featured.emptyText" severity="info" sx={{ "& .MuiAlert-message": { color: copy.emptyTextColor || "#1A1A2E" } }}>{emptyText}</Alert> : null
        ) : (
          <Box
            sx={{
              position: "relative",
              mx: { xs: -2, sm: -3, md: -6 },
              px: { xs: 2, sm: 3, md: 6 },
              overflowX: { xs: "auto", sm: "hidden" },
              overflowY: "hidden",
              py: 1.5,
              scrollSnapType: { xs: "x proximity", sm: "none" },
              overscrollBehaviorX: "contain",
              WebkitOverflowScrolling: "touch",
              maskImage: { xs: "none", sm: "linear-gradient(90deg, transparent 0%, black 7%, black 93%, transparent 100%)" },
              WebkitMaskImage: { xs: "none", sm: "linear-gradient(90deg, transparent 0%, black 7%, black 93%, transparent 100%)" },
              "@keyframes productsMarquee": { "0%": { transform: "translate3d(0,0,0)" }, "100%": { transform: "translate3d(-50%,0,0)" } },
              "&:hover .products-track": { animationPlayState: "paused" },
              "&:focus-within .products-track": { animationPlayState: "paused" },
            }}
          >
            <Box
              className="products-track"
              sx={{
                display: "flex",
                alignItems: "stretch",
                width: "max-content",
                animation: { xs: "none", sm: products.length > 1 ? `productsMarquee ${duration}s linear infinite` : "none" },
                willChange: { xs: "auto", sm: "transform" },
              }}
            >
              {repeated.map((product, index) => {
                const id = getProductId(product);
                const image = getProductImage(product);
                const category = getCategoryLabel(getProductCategory(product));
                const service = isService(product);
                const itemKind = getCatalogItemKind(product);
                const capabilities = getCatalogCapabilities(product);
                const subcategory = getCategoryLabel(getProductSubcategory(product));
                const description = getProductDescription(product);
                const durationLabel = formatDuration(getServiceDuration(product));
                const ctaLabel = catalogCardCtaLabel(product, cardConfig, legacyCtaLabel);
                const cardTarget = catalogCardTarget(product, cardConfig);
                const menuState = gastronomicMode ? getGastronomicMenu(product) : null;
                return (
                  <Box key={`${id || "product"}-${index}`} sx={{ flex: "0 0 auto", width: cardWidth, px: `${cardConfig.gap / 2}px`, display: { xs: index < products.length ? "block" : "none", sm: "block" }, scrollSnapAlign: { xs: "start", sm: "none" } }}>
                    <Card
                      data-landing-field="catalog.cards.surface"
                      onClick={() => openConfiguredUrl(cardTarget, navigate)}
                      sx={{
                        height: "100%",
                        display: "grid",
                        gridTemplateColumns: cardImageSide ? { xs: "1fr", sm: cardImageRight ? "62% 38%" : "38% 62%" } : "1fr",
                        gridTemplateRows: cardImageSide ? { xs: "auto 1fr auto", sm: "1fr auto" } : "auto 1fr auto",
                        overflow: "hidden",
                        cursor: "pointer",
                        position: "relative",
                        isolation: "isolate",
                        bgcolor: catalogCardBackgroundWithOpacity(cardConfig.backgroundColor, cardConfig.backgroundOpacity, "#FFFFFF"),
                        borderRadius: `${cardConfig.radius}px`,
                        border: cardConfig.borderColor ? `1px solid ${cardConfig.borderColor}` : undefined,
                        boxShadow: catalogCardBoxShadow(cardConfig),
                        "&::before": cardBackgroundImage ? {
                          content: '""',
                          position: "absolute",
                          inset: 0,
                          zIndex: 0,
                          pointerEvents: "none",
                          backgroundImage: `url("${String(cardBackgroundImage).replace(/"/g, "%22")}")`,
                          backgroundSize: cardConfig.backgroundImageFit,
                          backgroundPosition: cardConfig.backgroundImagePosition,
                          backgroundRepeat: "no-repeat",
                          opacity: cardConfig.backgroundImageOpacity,
                        } : undefined,
                        "& > *": { position: "relative", zIndex: 1 },
                        transition: "transform .28s ease, box-shadow .28s ease",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: catalogCardBoxShadow(cardConfig, {
                            legacyY: 12,
                            legacyBlur: 32,
                            fallback: "10px 12px 28px rgba(35,38,58,.2), -8px -8px 22px rgba(255,255,255,.88)",
                          }),
                        },
                        "&:hover .product-image": { transform: "scale(1.045)" },
                      }}
                    >
                      {cardConfig.showImage ? (
                        <Box sx={{
                          overflow: "hidden",
                          bgcolor: alpha("#EEF0F5", .55),
                          gridColumn: cardImageSide ? { xs: 1, sm: cardImageRight ? 2 : 1 } : 1,
                          gridRow: cardImageSide ? { xs: 1, sm: "1 / span 2" } : 1,
                          minHeight: cardImageSide ? { sm: cardConfig.imageHeight * imageScale } : 0,
                        }}>
                          <CardMedia
                            className="product-image"
                            component="img"
                            image={image}
                            alt={getProductName(product) || (service ? "Servicio" : "Producto")}
                            loading="lazy"
                            decoding="async"
                            onError={handleProductImageError}
                            sx={{
                              width: "100%",
                              height: cardImageSide ? { xs: cardConfig.imageHeight * imageScale, sm: "100%" } : cardConfig.imageHeight * imageScale,
                              minHeight: cardImageSide ? { sm: cardConfig.imageHeight * imageScale } : undefined,
                              objectFit: cardConfig.imageFit,
                              objectPosition: catalogCardImagePosition(cardConfig),
                              transition: "transform .4s ease",
                            }}
                          />
                        </Box>
                      ) : null}
                      <CardContent sx={{
                        flexGrow: 1,
                        p: 1.7,
                        pb: 1,
                        gridColumn: cardImageSide ? { xs: 1, sm: cardImageRight ? 1 : 2 } : 1,
                        gridRow: cardImageSide ? { xs: 2, sm: 1 } : 2,
                      }}>
                        {cardConfig.showCategory && category ? <Chip data-landing-field="catalog.cards.categoryColor" size="small" label={category} variant="outlined" sx={{ mb: 1, maxWidth: "100%", color: cardConfig.categoryColor || "#F35A3A", borderColor: cardConfig.categoryColor || "#F35A3A" }} /> : null}
                        {cardConfig.showSubcategory && subcategory ? <Typography data-landing-field="catalog.cards.subcategoryColor" variant="caption" sx={{ display: "block", mb: .5, color: cardConfig.subcategoryColor || "#5B5D73" }}>{subcategory}</Typography> : null}
                        {menuState?.available === false ? <Chip size="small" label="No disponible" color="warning" variant="outlined" sx={{ mb: 0.7 }} /> : null}
                        {cardConfig.showType ? <Typography data-landing-field="catalog.cards.typeColor" variant="overline" sx={{ display: "block", lineHeight: 1.2, color: cardConfig.typeColor || "#F35A3A" }}>{catalogItemLabel(product)}</Typography> : null}
                        {cardConfig.showTitle ? (
                          <Typography data-landing-field="catalog.cards.titleColor" variant="h5" sx={{ ...safeTitleSx, minHeight: 54, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", fontSize: `${1.5 * titleWordFitScale(getProductName(product) || (service ? "Servicio" : "Producto"))}rem`, color: cardConfig.titleColor || "#1A1A2E" }}>
                            {getProductName(product) || (service ? "Servicio" : "Producto")}
                          </Typography>
                        ) : null}
                        {cardConfig.showDescription && description ? (
                          <Typography data-landing-field="catalog.cards.descriptionColor" variant="body2" sx={{ mt: .7, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", color: cardConfig.descriptionColor || "#5B5D73" }}>
                            {description}
                          </Typography>
                        ) : null}
                        {cardConfig.showDuration && (itemKind === "service" || itemKind === "hybrid") && durationLabel ? (
                          <Typography data-landing-field="catalog.cards.descriptionColor" variant="caption" sx={{ display: "block", mt: .7, color: cardConfig.descriptionColor || "#5B5D73" }}>Duración: {durationLabel}</Typography>
                        ) : null}
                        {cardConfig.showCapabilities && (capabilities.reservable || capabilities.shippable) ? (
                          <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap sx={{ mt: .7 }}>
                            {capabilities.reservable ? <Chip size="small" label="Reserva online" /> : null}
                            {capabilities.shippable ? <Chip size="small" label="Entrega" /> : null}
                          </Stack>
                        ) : null}
                        {cardConfig.showPrice ? (
                          <Typography data-landing-field="catalog.cards.priceColor" variant="caption" sx={{ display: "block", mt: 1, color: cardConfig.priceColor || "#5B5D73" }}>
                            {service ? "Valor" : "Precio"}
                          </Typography>
                        ) : null}
                        {cardConfig.showPrice ? (
                          <Box data-landing-field="catalog.cards.priceColor" sx={safeTextSx}>
                            <ProductPriceDisplay product={product} money={(value) => formatMoney(value, getProductCurrency(product, null, payments?.currency), payments?.locale)} color={cardConfig.priceColor || "#19242F"} />
                          </Box>
                        ) : null}
                      </CardContent>
                      {cardConfig.showButton && ctaLabel ? (
                        <CardActions sx={{
                          p: 1.7,
                          pt: .5,
                          gridColumn: cardImageSide ? { xs: 1, sm: cardImageRight ? 1 : 2 } : 1,
                          gridRow: cardImageSide ? { xs: 3, sm: 2 } : 3,
                        }}>
                          <Button
                            data-landing-field={service ? "catalog.cards.serviceCtaLabel" : "catalog.cards.ctaLabel"}
                            fullWidth
                            variant="contained"
                            onClick={(event) => { event.stopPropagation(); openConfiguredUrl(cardTarget, navigate); }}
                            sx={catalogCardButtonSx(cardConfig)}
                          >
                            {ctaLabel}
                          </Button>
                        </CardActions>
                      ) : null}
                    </Card>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
